import { useMicroCMSClient } from '@/wabunka-src/common/composables/query/useMicroCMSClient';
import { buildMinimumFeaturedListViewModel, type MinimumFeaturedListViewModel, type MinimumFeaturedResponse } from '@/wabunka-src/common/features/Featured/models/minimunFeatured/minimumFeatured';

// カード表示（画像・見出し・説明文）に必要な最小フィールドのみ取得する。
// id は行き先タグ webPath（= LP /{webPath} の解決キー）との突合キーになる。
const FEATURED_FIELDS = ['id', 'titleEn', 'backgroundWideMedias', 'backgroundTallMedias', 'type', 'summary'];
const FETCH_LIMIT = 100;

/**
 * 一覧マップのエリアラベル層が参照する「最新の」microCMS featured(destination) を取得する Composable。
 *
 * - `server: false` + `lazy: true` + `immediate: false` でクライアント側のみ・非ブロッキングに取得し、
 *   呼び出し側が必要になった時点で `execute()` する。
 * - 常に最新の microCMS を叩くため、featured の入稿・削除が即座に選択可能な行き先へ反映される。
 * - **返却順がそのままカードの並び順になる**（buildSelectableDestinations が microCMS 順に整列する）。
 *   編集者が管理画面の「コンテンツの並び替え」で決めた順を使いたいため `orders` は指定しない。
 *   ここで `orders` を足すと管理画面の並び替えが効かなくなるので注意。
 * - 失敗時は空配列に degrade する（エリアラベルが出ないだけでページは落ちない）。
 */
export const useLatestFeaturedDestinationsQuery = () => {
  const { getList } = useMicroCMSClient();

  const { data, status, execute } = useAsyncData<MinimumFeaturedListViewModel>(
    'latest-featured-destinations',
    async () => {
      const res = await getList<MinimumFeaturedResponse>({
        endpoint: 'en-featured',
        queries: { fields: FEATURED_FIELDS, filters: 'type[contains]destination', limit: FETCH_LIMIT },
      });
      // ページングしていないため、上限を超えると超過分の行き先が静かに選択不可になる。
      // 入稿が増えたら FETCH_LIMIT の引き上げかページング対応が要るので、開発時に気付けるようにする。
      if (import.meta.dev && res.totalCount > FETCH_LIMIT) {
        console.warn(`[featured-destinations] microCMS featured(destination) が ${res.totalCount} 件あり、取得上限 ${FETCH_LIMIT} 件を超えています`);
      }
      return buildMinimumFeaturedListViewModel(res.contents);
    },
    { server: false, lazy: true, immediate: false, default: () => [] },
  );

  const destinationContentList = computed<MinimumFeaturedListViewModel>(() => data.value ?? []);
  /** 取得に失敗したか。`execute()` は reject しないため、呼び出し側はこれで失敗を知る。 */
  const hasErrored = computed<boolean>(() => status.value === 'error');

  return { destinationContentList, hasErrored, execute };
};
